import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';
import { Merchant, Device, Activity, SystemHealth } from '../components/Dashboard/DashboardAdmin/types';
import { SYSTEM_HEALTH_DEFAULTS } from '../components/Dashboard/DashboardAdmin/constants';
import { generateActivitiesFromData } from '../components/Dashboard/DashboardAdmin/utils';

export interface AdminDashboardState {
  merchants: Merchant[];
  devices: Device[];
  activities: Activity[];
  systemHealth: SystemHealth;
  loading: {
    merchants: boolean;
    devices: boolean;
    activities: boolean;
    systemHealth: boolean;
  };
  error: string | null;
}

const initialState: AdminDashboardState = {
  merchants: [],
  devices: [],
  activities: [],
  systemHealth: { ...SYSTEM_HEALTH_DEFAULTS },
  loading: {
    merchants: false,
    devices: false,
    activities: false,
    systemHealth: false,
  },
  error: null,
};

export const fetchAdminMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>('adminDashboard/fetchMerchants', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('merchant', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants'
    );
  }
});

export const fetchAdminDevices = createAsyncThunk<
  Device[],
  void,
  { rejectValue: string }
>('adminDashboard/fetchDevices', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('devices', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch devices'
    );
  }
});

export const fetchAdminActivities = createAsyncThunk<
  Activity[],
  void,
  { rejectValue: string; state: { adminDashboard: AdminDashboardState } }
>('adminDashboard/fetchActivities', async (_, { getState }) => {
  try {
    const response = await API.get('analytics/admin/activities', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.success) {
      return response.data.activities || [];
    }
    const { merchants, devices } = getState().adminDashboard;
    return generateActivitiesFromData(merchants, devices);
  } catch {
    const { merchants, devices } = getState().adminDashboard;
    return generateActivitiesFromData(merchants, devices);
  }
});

export const fetchAdminSystemHealth = createAsyncThunk<
  SystemHealth,
  void,
  { rejectValue: string }
>('adminDashboard/fetchSystemHealth', async () => {
  try {
    const response = await API.get('analytics/admin/system-health', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.success) {
      return {
        uptime: response.data.uptime ?? SYSTEM_HEALTH_DEFAULTS.uptime,
        apiResponse: response.data.apiResponse ?? SYSTEM_HEALTH_DEFAULTS.apiResponse,
        database: response.data.database ?? SYSTEM_HEALTH_DEFAULTS.database,
      };
    }
    return { ...SYSTEM_HEALTH_DEFAULTS };
  } catch {
    return { ...SYSTEM_HEALTH_DEFAULTS };
  }
});

export const fetchAllAdminDashboardData = createAsyncThunk<
  void,
  void,
  { dispatch: any }
>('adminDashboard/fetchAll', async (_, { dispatch }) => {
  await Promise.all([
    dispatch(fetchAdminMerchants()),
    dispatch(fetchAdminDevices()),
  ]);
  await Promise.all([
    dispatch(fetchAdminActivities()),
    dispatch(fetchAdminSystemHealth()),
  ]);
});

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',
  initialState,
  reducers: {
    clearAdminDashboardError: (state) => {
      state.error = null;
    },
    setActivities: (state, action: PayloadAction<Activity[]>) => {
      state.activities = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchAdminMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchAdminMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload || 'Failed to fetch merchants';
      })
      .addCase(fetchAdminDevices.pending, (state) => {
        state.loading.devices = true;
        state.error = null;
      })
      .addCase(fetchAdminDevices.fulfilled, (state, action) => {
        state.loading.devices = false;
        state.devices = action.payload;
      })
      .addCase(fetchAdminDevices.rejected, (state, action) => {
        state.loading.devices = false;
        state.error = action.payload || 'Failed to fetch devices';
      })
      .addCase(fetchAdminActivities.pending, (state) => {
        state.loading.activities = true;
      })
      .addCase(fetchAdminActivities.fulfilled, (state, action) => {
        state.loading.activities = false;
        state.activities = action.payload;
      })
      .addCase(fetchAdminActivities.rejected, (state, action) => {
        state.loading.activities = false;
        state.error = action.payload || 'Failed to fetch activities';
      })
      .addCase(fetchAdminSystemHealth.pending, (state) => {
        state.loading.systemHealth = true;
      })
      .addCase(fetchAdminSystemHealth.fulfilled, (state, action) => {
        state.loading.systemHealth = false;
        state.systemHealth = action.payload;
      })
      .addCase(fetchAdminSystemHealth.rejected, (state, action) => {
        state.loading.systemHealth = false;
        state.error = action.payload || 'Failed to fetch system health';
      });
  },
});

export const { clearAdminDashboardError, setActivities } = adminDashboardSlice.actions;
export default adminDashboardSlice.reducer;
